/**
 * The WS client (PLAN §6.5, the contract's `ws.ts`): subscribe to a rundown,
 * stream `row.state`, keep the socket alive, reconnect with backoff, and catch
 * up after every reconnect.
 *
 * **The catch-up handshake** is the contract's: send `subscribe`, wait for
 * `subscribed`, and only then pull `GET …/rows?changedSince=<last cursor>` —
 * subscribing first means nothing written during the pull is missed. A row
 * that arrives both ways is delivered twice; a consumer merges, never appends
 * (applying a row twice is harmless, `mergeRows` is idempotent).
 *
 * **Presence** (PLAN §6.6, the contract's presence section): with a
 * `presence` state, the client announces it after every `subscribed` and
 * re-announces every PRESENCE_REFRESH_MS; either way it reports the server's
 * `presence.state` lists — applying one only if its `ts` is not older than the
 * last (cloud fan-out can reorder them) — and `presence.leave`s. The state
 * can change while connected (`setPresence`, the SPA's focus moving between
 * rows): the new one is announced at once on a subscribed socket and becomes
 * what the refresh and every later `subscribed` send.
 *
 * The token rides in the query string (`?token=`), the form both hosts accept
 * from a non-browser client.
 */
import type { Row } from '@acp/domain/row.js';
import { PRESENCE_REFRESH_MS, parseServerMessage, type PresenceMember, type PresenceState, type ServerMessage } from '@acp/api-contract/ws.js';
import type { SocketFactory, SocketLike, Timers } from './ports.js';

/** PLAN §6.5: API Gateway idles a socket out at 10 minutes; ping every 5. */
export const KEEPALIVE_MS = 5 * 60 * 1000;
/** Reconnect backoff: 1 s doubling to at most 30 s, reset by a successful subscribe. */
export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;

export function backoffMs(attempt: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** attempt);
}

export type WatchEvent =
  | { readonly type: 'subscribed'; readonly cursor: string }
  | { readonly type: 'row'; readonly row: Row; readonly source: 'live' | 'catch-up'; readonly clientOpId: string | null }
  | { readonly type: 'patch'; readonly message: Extract<ServerMessage, { type: 'row.patch' }> }
  | { readonly type: 'nack'; readonly message: Extract<ServerMessage, { type: 'nack' }> }
  | { readonly type: 'presence'; readonly members: readonly PresenceMember[] }
  | { readonly type: 'presence-leave'; readonly connectionId: string; readonly userId: string }
  | { readonly type: 'disconnected'; readonly code: number; readonly reason: string; readonly retryInMs: number }
  | { readonly type: 'warning'; readonly message: string };

export type WatchOptions = {
  readonly wsUrl: string;
  readonly rundownId: string;
  /** The `tenantId` routing hint of the envelope (the server acts on the token's tenant, D13). */
  readonly tenantId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  /** `GET …/rows?changedSince=`, all pages. */
  readonly catchUp: (cursor: string) => Promise<{ rows: readonly Row[]; cursor: string | null }>;
  /** Start from this cursor (catch up on the first subscribe too), or null to start live. */
  readonly since: string | null;
  /** Announce this presence on the rundown (and keep it alive), or null to watch without being present. */
  readonly presence: PresenceState | null;
  readonly onEvent: (event: WatchEvent) => void;
};

export type WatchHandle = {
  readonly stop: () => void;
  /** Resolves after `stop()`; rejects if (re)connecting fails fatally (no token). */
  readonly done: Promise<void>;
  /**
   * Change the announced presence (null: stop announcing; the server expires
   * the member after PRESENCE_TTL_MS). Sent at once when subscribed, else
   * with the next `subscribed`.
   */
  readonly setPresence: (state: PresenceState | null) => void;
};

export function watchRundown(opts: WatchOptions): WatchHandle {
  let stopped = false;
  let socket: SocketLike | null = null;
  let attempt = 0;
  let cursor: string | null = opts.since;
  let cancelKeepalive: (() => void) | null = null;
  let cancelPresence: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  /** The `ts` of the newest `presence.state` applied; an older one arriving later is stale. */
  let presenceTs = -1;
  let presence: PresenceState | null = opts.presence;
  /** Whether the current socket has seen `subscribed` (presence may only be announced after it). */
  let subscribed = false;
  const { promise: done, resolve: resolveDone, reject: rejectDone } = Promise.withResolvers<void>();

  const envelope = (type: 'subscribe' | 'ping') =>
    type === 'subscribe'
      ? { type, tenantId: opts.tenantId, rundownId: opts.rundownId, payload: {}, ts: opts.nowMs() }
      : { type, tenantId: opts.tenantId, payload: {}, ts: opts.nowMs() };

  const announce = (s: SocketLike, state: PresenceState): void => {
    s.send(JSON.stringify({ type: 'presence.update', tenantId: opts.tenantId, rundownId: opts.rundownId, payload: { state }, ts: opts.nowMs() }));
  };

  // After each `subscribed` (a reconnect is a new connection, present nowhere yet), then on a timer.
  const startPresence = (): void => {
    const s = socket;
    const state = presence;
    cancelPresence?.();
    cancelPresence = null;
    if (s === null || state === null) return;
    announce(s, state);
    cancelPresence = opts.timers.every(PRESENCE_REFRESH_MS, () => announce(s, state));
  };

  // Cursors are OPAQUE (`cursorSchema`: the node's are `<storeId>.<seq>`), so
  // they are never compared or taken from a row's `changedAt`: the cursor is
  // always the last one a server handed out. The subscription's cursor covers
  // everything after it; the catch-up from the previous cursor covers what
  // came before, and its listing's cursor (read after the subscribe) is the
  // newest point both paths have reached.
  const catchUp = async (serverCursor: string): Promise<void> => {
    const from = cursor;
    cursor = serverCursor;
    if (from === null) return;
    try {
      const result = await opts.catchUp(from);
      for (const row of result.rows) opts.onEvent({ type: 'row', row, source: 'catch-up', clientOpId: null });
      cursor = result.cursor ?? serverCursor;
    } catch (err) {
      // Live events keep flowing; the gap is reported, and the next reconnect retries from `from`.
      cursor = from;
      opts.onEvent({ type: 'warning', message: `catch-up failed: ${err instanceof Error ? err.message : String(err)}` });
    }
  };

  const onMessage = async (data: unknown): Promise<void> => {
    const parsed = parseServerMessage(typeof data === 'string' ? data : String(data));
    if (!parsed.ok) {
      opts.onEvent({ type: 'warning', message: `unparseable frame: ${parsed.error}` });
      return;
    }
    const message = parsed.message;
    switch (message.type) {
      case 'subscribed':
        attempt = 0;
        subscribed = true;
        opts.onEvent({ type: 'subscribed', cursor: message.payload.cursor });
        startPresence();
        await catchUp(message.payload.cursor);
        return;
      case 'presence.state':
        if (message.ts < presenceTs) return;
        presenceTs = message.ts;
        opts.onEvent({ type: 'presence', members: message.payload.members });
        return;
      case 'presence.leave':
        opts.onEvent({ type: 'presence-leave', connectionId: message.payload.connectionId, userId: message.payload.userId });
        return;
      case 'row.state':
        // A live row does not move the cursor: rows after the subscription's cursor may repeat on the
        // next catch-up, and merging a row twice is harmless.
        opts.onEvent({ type: 'row', row: message.payload.row, source: 'live', clientOpId: message.payload.clientOpId });
        return;
      case 'row.patch':
        opts.onEvent({ type: 'patch', message });
        return;
      case 'nack':
        opts.onEvent({ type: 'nack', message });
        return;
      case 'pong':
        return;
    }
  };

  const connect = async (): Promise<void> => {
    let token: string;
    try {
      token = await opts.token();
    } catch (err) {
      stopped = true;
      rejectDone(err);
      return;
    }
    if (stopped) return;
    const s = opts.openSocket(`${opts.wsUrl}?token=${encodeURIComponent(token)}`);
    socket = s;
    subscribed = false;
    s.onopen = () => {
      s.send(JSON.stringify(envelope('subscribe')));
      cancelKeepalive = opts.timers.every(KEEPALIVE_MS, () => s.send(JSON.stringify(envelope('ping'))));
    };
    s.onmessage = (event) => {
      void onMessage(event.data);
    };
    s.onerror = () => undefined; // a close follows; that is where the retry is decided
    s.onclose = (event) => {
      cancelKeepalive?.();
      cancelKeepalive = null;
      cancelPresence?.();
      cancelPresence = null;
      socket = null;
      subscribed = false;
      if (stopped) return;
      const retryInMs = backoffMs(attempt);
      attempt += 1;
      opts.onEvent({ type: 'disconnected', code: event.code, reason: event.reason, retryInMs });
      cancelRetry = opts.timers.after(retryInMs, () => {
        cancelRetry = null;
        void connect();
      });
    };
  };

  void connect();

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      cancelRetry?.();
      cancelKeepalive?.();
      cancelPresence?.();
      socket?.close();
      resolveDone();
    },
    done,
    setPresence: (state) => {
      presence = state;
      if (subscribed && !stopped) startPresence();
    },
  };
}
